"use client";

import { useEffect, useState } from "react";
import { DailyChallengeState } from "@/lib/daily/types";
import { loadDailyChallengeState } from "@/lib/daily/storageManager";
import { generateCombinedShare, generateShareCardCanvas } from "@/lib/daily/shareGenerator";
import { loadFinWordState } from "@/lib/daily/storageManager";
import { loadQuizState } from "@/lib/daily/storageManager";
import { formatDatePretty } from "@/lib/daily/dateUtils";
import CompletionBadge from "./shared/CompletionBadge";
import DailyProgress from "./shared/DailyProgress";
import ShareButton from "./shared/ShareButton";
import { DAILY_FEATURES_CONFIG } from "@/lib/constants/daily";
import { Type, Brain, ArrowRight, Flame } from "lucide-react";
import Link from "next/link";
import DailyLoader from "./shared/DailyLoader";
import YouMightAlsoLike from "@/components/blog/YouMightAlsoLike";
import { getDynamicYMALItems, type YMALPoolItem } from "@/lib/blog/ymal-mapper";

interface DailyHubProps {
  // Light blog pool computed server-side by the /daily page (the pool source
  // imports the full posts module, which must never enter a client bundle).
  // Rotation stays client-side so the picks still change with the daily date.
  readonly ymalPool?: readonly YMALPoolItem[];
}

export default function DailyHub({ ymalPool = [] }: DailyHubProps) {
  const [hubState, setHubState] = useState<DailyChallengeState | null>(null);
  const [wordAttempts, setWordAttempts] = useState(0);
  const [wordWon, setWordWon] = useState(false);
  const [quizScore, setQuizScore] = useState(0);

  const loadAllStates = () => {
    const state = loadDailyChallengeState();
    setHubState(state);

    // Get details for combined sharing
    const wordState = loadFinWordState(state.date);
    if (wordState) {
      setWordAttempts(wordState.guesses.length);
      setWordWon(wordState.status === "won");
    }

    const quizState = loadQuizState(state.date);
    if (quizState) {
      setQuizScore(quizState.score);
    }
  };

  // Load and listen to localstorage updates (sync multiple tabs)
  useEffect(() => {
    loadAllStates();

    const handleStorageChange = () => {
      loadAllStates();
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  if (!hubState) {
    return <DailyLoader message="Loading today's challenge..." />;
  }

  // Count completions
  let completedCount = 0;
  if (hubState.wordCompleted) completedCount++;
  if (hubState.quizCompleted) completedCount++;

  const featureCompletions: Record<string, boolean> = {
    word: hubState.wordCompleted,
    quiz: hubState.quizCompleted,
  };

  const featureIcons: Record<string, any> = {
    word: <Type size={22} className="type-icon-anim" />,
    quiz: <Brain size={22} className="brain-icon-anim" />,
  };

  const featureXP: Record<string, number> = {
    word: 50,
    quiz: 50,
  };

  const prettyDate = formatDatePretty(hubState.date);

  // Combined share details
  const shareText = generateCombinedShare(
    prettyDate,
    hubState.wordCompleted,
    wordAttempts,
    wordWon,
    hubState.quizCompleted,
    quizScore,
    hubState.streak
  );

  const shareCanvasDataUrl = generateShareCardCanvas(
    "Daily Finance Challenge",
    prettyDate,
    "Daily Streak",
    `${hubState.streak} days`,
    [
      `FinWord: ${hubState.wordCompleted ? (wordWon ? `Won (${wordAttempts}/6)` : "Lost") : "Incomplete"}`,
      `Quiz: ${hubState.quizCompleted ? `${quizScore}/4 Correct` : "Incomplete"}`,
    ]
  );

  const pct = Math.round((completedCount / 2) * 100);

  // Gamified Motivational message configs
  let motivationTitle = "Quests Await!";
  let motivationDesc = "Complete today's challenges to gain financial wisdom & build your daily streak.";
  if (completedCount === 1) {
    motivationTitle = "Halfway There!";
    motivationDesc = "One more challenge to lock in today's knowledge!";
  } else if (completedCount === 2) {
    motivationTitle = "Habit Mastered!";
    motivationDesc = "All challenges complete. You've earned today's financial literacy crown!";
  }

  return (
    <div className="daily-hub-wrapper">
      {/* Header Text & Date */}
      <div className="daily-header" style={{ marginBottom: "1.25rem" }}>
        <h1 className="daily-title page-title">
          Daily Finance Challenge 2026
        </h1>
        <p className="daily-subtitle page-description">
          Build financial literacy with today's FinWord puzzle and 4-question finance quiz. Complete both daily challenges to build your streak.
        </p>
        <div style={{ fontSize: "var(--text-sm)", fontWeight: "var(--fw-semibold)", color: "var(--text-muted)", marginTop: "var(--space-2)", display: "inline-flex", alignItems: "center", gap: "6px" }}>
          <span>📅 Puzzle date: {prettyDate} (UTC) — everyone worldwide gets the same daily puzzle</span>
        </div>
      </div>

      {/* Progress & Streak Widget (Full 100% Width) */}
      <div
        className="daily-streak-widget-bar"
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "1.25rem",
          padding: "14px 20px",
          background: "var(--bg-surface)",
          border: "1px solid var(--border-color)",
          borderRadius: "var(--radius-lg)",
          boxShadow: "var(--shadow-sm)",
          marginBottom: "1.5rem",
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "1.25rem" }}>
          {/* Compact Circular Progress Ring (2/2) */}
          <DailyProgress completedCount={completedCount} totalCount={2} compact={true} />

          {/* Streak Badge & Details */}
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <Flame 
              size={26} 
              className={hubState.streak > 0 ? "flame-icon-svg" : ""} 
              style={{ color: hubState.streak > 0 ? "var(--accent-orange)" : "var(--text-muted)", opacity: hubState.streak > 0 ? 1 : 0.4 }} 
              fill={hubState.streak > 0 ? "currentColor" : "none"}
            />
            <div>
              <div style={{ fontSize: "var(--text-base)", fontWeight: "var(--fw-extrabold)", color: hubState.streak > 0 ? "var(--text-primary)" : "var(--text-muted)", lineHeight: 1.1 }}>
                {hubState.streak > 0 ? `${hubState.streak} Day Streak` : "0 Day Streak"}
              </div>
              <div style={{ fontSize: "var(--text-2xs)", color: completedCount === 2 ? "var(--success-fg)" : "var(--text-muted)", fontWeight: "var(--fw-bold)", textTransform: "uppercase", marginTop: "3px" }}>
                {completedCount === 2 ? "100% Completed!" : `${completedCount}/2 COMPLETED`}
              </div>
            </div>
          </div>
        </div>

        {/* Right side action / hint */}
        {completedCount > 0 ? (
          <div className="anim-fade-in">
            <ShareButton
              text={shareText}
              canvasDataUrl={shareCanvasDataUrl}
              url="https://financelives.com/daily"
            />
          </div>
        ) : (
          <div style={{ fontSize: "var(--text-xs)", fontWeight: "var(--fw-bold)", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
            Complete challenges to build streak 🔥
          </div>
        )}
      </div>

      {/* Feature Grid */}
      <div className="daily-hub-grid">
        {DAILY_FEATURES_CONFIG.map((feat) => {
          const isDone = featureCompletions[feat.id] || false;
          const hasPracticeMode = feat.id === "word" || feat.id === "quiz";
          
          return (
            <div key={feat.id} className="daily-feature-card-wrapper">
              <Link
                href={feat.path}
                className={`daily-feature-card ${isDone ? "completed" : ""}`}
              >
                <div>
                  <div className="daily-card-header">
                    <div className="daily-feature-icon-wrapper">
                      {featureIcons[feat.id]}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <span 
                        style={{ 
                          fontSize: "var(--text-2xs)",
                          fontWeight: "var(--fw-extrabold)", 
                          // --success-fg (not --primary) on the light tint: AA contrast
                          color: isDone ? "var(--success-fg)" : "var(--accent-orange)",
                          backgroundColor: isDone ? "var(--primary-light)" : "var(--accent-orange-light)",
                          padding: "3px 8px", 
                          borderRadius: "var(--radius-full)",
                          letterSpacing: "0.02em"
                        }}
                      >
                        {isDone ? "✓ Claimed" : `+${featureXP[feat.id]} XP`}
                      </span>
                      <CompletionBadge completed={isDone} />
                    </div>
                  </div>
                  <h2 className="daily-card-title">{feat.name}</h2>
                  <p className="daily-card-desc">{feat.description}</p>
                </div>

                <div className="daily-card-action">
                  <span>{isDone ? "Review Today's Challenge" : "Start Challenge"}</span>
                  <ArrowRight size={16} />
                </div>
              </Link>

              {hasPracticeMode && (
                <Link
                  href={`${feat.path}?mode=practice`}
                  className="practice-link-btn"
                >
                  Practice Unlimited Mode →
                </Link>
              )}
            </div>
          );
        })}
      </div>

      <div className="section-divider"></div>
      <YouMightAlsoLike noMargin={true} items={getDynamicYMALItems([...ymalPool], "daily-hub-" + (hubState?.date || "today"))} />
    </div>
  );
}
